import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle, Calendar, Send, Users, UserPlus, UserX, Copy, Check, Eye, Code } from 'lucide-react';
import { Card } from '../components/ui/card';
import { getAdminHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Parse WhatsApp-like markdown (*bold*, _italic_, ~strike~) preserving line breaks.
const renderWhatsAppMessage = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, li) => {
    const parts = [];
    // regex matching *bold*, _italic_, ~strike~ (non-greedy), else plain
    const regex = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) parts.push(<span key={`${li}-${key++}`}>{line.slice(lastIndex, match.index)}</span>);
      const token = match[0];
      if (token.startsWith('*')) parts.push(<strong key={`${li}-${key++}`} className="font-bold text-gray-900">{token.slice(1, -1)}</strong>);
      else if (token.startsWith('_')) parts.push(<em key={`${li}-${key++}`} className="italic text-gray-500">{token.slice(1, -1)}</em>);
      else parts.push(<s key={`${li}-${key++}`}>{token.slice(1, -1)}</s>);
      lastIndex = match.index + token.length;
    }
    if (lastIndex < line.length) parts.push(<span key={`${li}-${key++}`}>{line.slice(lastIndex)}</span>);
    return (
      <div key={li} className={line.trim() === '' ? 'h-2' : ''}>
        {parts.length > 0 ? parts : (line || '\u00A0')}
      </div>
    );
  });
};

const WhatsAppPreview = ({ message }) => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return (
    <div
      className="rounded-xl p-4 border border-gray-200"
      style={{
        backgroundColor: '#E5DDD5',
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><circle cx='20' cy='20' r='1' fill='%23D5CDC4' opacity='0.4'/></svg>\")",
      }}
    >
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-[#DCF8C6] rounded-2xl rounded-tr-sm px-3 py-2 shadow-sm relative">
          <div className="text-sm text-gray-800 leading-relaxed font-sans whitespace-pre-wrap">
            {renderWhatsAppMessage(message)}
          </div>
          <div className="flex justify-end items-center gap-1 mt-1 -mb-0.5">
            <span className="text-[10px] text-gray-500">{hh}:{mm}</span>
            <svg width="14" height="10" viewBox="0 0 16 15" fill="#4FC3F7"><path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/></svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const RelatorioWhatsApp = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(params.get('date') || new Date().toISOString().split('T')[0]);
  const [meetingId, setMeetingId] = useState(params.get('meeting_id') || '');
  const [copied, setCopied] = useState('');
  const [viewMode, setViewMode] = useState({}); // {groupId: 'preview' | 'text'}

  const fetchReport = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (meetingId) q.append('meeting_id', meetingId);
      else if (selectedDate) q.append('date', selectedDate);
      const res = await axios.get(`${API}/reports/meeting-whatsapp?${q.toString()}`, { headers: getAdminHeaders() });
      setReport(res.data);
      if (res.data.error) toast.error(res.data.error);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar relatorio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (meetingId || selectedDate) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
      toast.success('Texto copiado!');
    } catch (e) { toast.error('Erro ao copiar'); }
  };

  const sendAllLideres = (g) => {
    const links = g.whatsapp_links || [];
    if (links.length === 0) { toast.error('Nenhum lider com celular cadastrado'); return; }
    links.forEach((l, i) => setTimeout(() => window.open(l.url, '_blank'), i * 400));
    toast.success(`Abrindo WhatsApp de ${links.length} lider(es)`);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-20">
      <div className="bg-[#1A4D2E] text-white px-4 py-5 shadow-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button data-testid="btn-back" onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <MessageCircle className="w-6 h-6 text-[#F4D35E]" />
          <h1 className="font-nunito font-bold text-lg sm:text-xl">Relatorio WhatsApp - Lideres de Discipulado</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4 sm:py-6">
        {/* Filters */}
        <Card className="bg-white rounded-2xl p-4 mb-4 shadow-sm border border-[#E1ECE5]">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Data do encontro</label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); setMeetingId(''); }}
                  data-testid="report-date-input"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm" />
              </div>
            </div>
            <button onClick={fetchReport} disabled={loading} data-testid="generate-report-btn"
              className="w-full sm:w-auto bg-[#1A4D2E] hover:bg-[#145A32] disabled:opacity-40 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all">
              {loading ? 'Gerando...' : 'Gerar Relatorio'}
            </button>
          </div>
        </Card>

        {/* Groups */}
        {report && report.groups && report.groups.length > 0 ? (
          <div className="space-y-4">
            {report.groups.map(g => (
              <Card key={g.group.id} className="bg-white rounded-2xl p-4 sm:p-5 shadow-md border border-[#E1ECE5]" data-testid={`group-card-${g.group.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <h3 className="font-nunito font-bold text-base sm:text-lg text-[#1A4D2E]">{g.group.nome}</h3>
                    <p className="text-xs text-gray-500">{g.group.faixa_etaria} · {g.group.sexo}</p>
                    {g.lideres.length > 0 ? (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-semibold">Lideres:</span> {g.lideres.map(l => l.nome + (l.celular ? '' : ' (sem cel)')).join(', ')}
                      </p>
                    ) : (
                      <p className="text-xs text-red-500 mt-1">Sem lider cadastrado para este grupo</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleCopy(g.message, g.group.id)}
                      data-testid={`copy-msg-${g.group.id}`}
                      className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-xs font-semibold transition-all">
                      {copied === g.group.id ? <><Check className="w-3.5 h-3.5 text-green-600" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                    </button>
                    <button onClick={() => sendAllLideres(g)} disabled={!g.whatsapp_links || g.whatsapp_links.length === 0}
                      data-testid={`send-wa-${g.group.id}`}
                      className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-3 py-2 rounded-xl text-xs font-semibold transition-all">
                      <Send className="w-3.5 h-3.5" /> Enviar WhatsApp
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-green-50 rounded-xl p-2 text-center">
                    <Users className="w-4 h-4 mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold text-green-700">{g.presentes.length}</p>
                    <p className="text-[10px] text-green-600 font-semibold">Presentes</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-2 text-center">
                    <UserPlus className="w-4 h-4 mx-auto text-amber-600 mb-1" />
                    <p className="text-lg font-bold text-amber-700">{g.visitantes.length}</p>
                    <p className="text-[10px] text-amber-600 font-semibold">Visitantes</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-2 text-center">
                    <UserX className="w-4 h-4 mx-auto text-red-600 mb-1" />
                    <p className="text-lg font-bold text-red-700">{g.ausentes.length}</p>
                    <p className="text-[10px] text-red-600 font-semibold">Ausentes</p>
                  </div>
                </div>

                <details className="border border-gray-100 rounded-xl" data-testid={`msg-details-${g.group.id}`}>
                  <summary className="px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5" /> Ver mensagem
                  </summary>
                  <div className="p-3 bg-gray-50 rounded-b-xl space-y-2">
                    {/* Mode toggle */}
                    <div className="flex gap-1 bg-white rounded-lg p-1 w-fit border border-gray-200">
                      <button
                        data-testid={`view-preview-${g.group.id}`}
                        onClick={() => setViewMode(prev => ({ ...prev, [g.group.id]: 'preview' }))}
                        className={`px-3 py-1 rounded-md text-[11px] font-semibold flex items-center gap-1 transition-all ${(viewMode[g.group.id] || 'preview') === 'preview' ? 'bg-[#1A4D2E] text-white' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        <Eye className="w-3 h-3" /> Preview
                      </button>
                      <button
                        data-testid={`view-text-${g.group.id}`}
                        onClick={() => setViewMode(prev => ({ ...prev, [g.group.id]: 'text' }))}
                        className={`px-3 py-1 rounded-md text-[11px] font-semibold flex items-center gap-1 transition-all ${viewMode[g.group.id] === 'text' ? 'bg-[#1A4D2E] text-white' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        <Code className="w-3 h-3" /> Texto
                      </button>
                    </div>
                    {(viewMode[g.group.id] || 'preview') === 'preview' ? (
                      <WhatsAppPreview message={g.message} />
                    ) : (
                      <pre className="p-3 text-xs whitespace-pre-wrap font-mono text-gray-700 bg-white rounded-xl border border-gray-200 overflow-x-auto">{g.message}</pre>
                    )}
                  </div>
                </details>
              </Card>
            ))}
          </div>
        ) : report ? (
          <Card className="bg-white rounded-2xl p-8 text-center text-gray-500 text-sm">
            Nenhum grupo com dados para esta data.
          </Card>
        ) : null}
      </div>
    </div>
  );
};

export default RelatorioWhatsApp;
